import {
  IExperienceTemplate,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import {
  ScheduleChannel,
  ScheduleChannelType
} from '@dadohr/shared/domainTypes/schedules';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { IconButton, Tooltip } from '@material-ui/core';
import { compact, flatten, isEqual, uniqBy } from 'lodash';
import { Mail, MessageSquare, Slack } from 'lucide-react';
import React, { SVGProps, useMemo, useRef } from 'react';
import { WithShape } from '../../../../components/Charts/Util';
import { useTranslation, useUnsafeTranslation } from '../../../../i18next';
import { ReactComponent as TeamsIcon } from '../../../../icons/microsoft_teams_icon.svg';
import { channelToKey, channelToString } from '../../../../services/channels';
import { ReactComponent as MailPersonal } from './MailPersonal.svg';
import { ReactComponent as MailWork } from './MailWork.svg';

export type ChannelDescriptor = {
  channel: ScheduleChannel;
  color: string;
  role: string;
  key: string;
};

export const channelToKeyWithPrefix = (prefix: string, c: ScheduleChannel) =>
  `${prefix}-${channelToKey(c)}`;

export const useChannelDescriptors = (d: IExperienceTemplate) => {
  const prevResult = useRef<{
    [key: string]: ChannelDescriptor;
  }>({});
  const {
    custom: { colors }
  } = useTheme();
  const { t } = useTranslation();
  const participantLabel = t('Participant');
  return useMemo(() => {
    const toDescriptor = (actorKey: string, channel: ScheduleChannel) => {
      if (actorKey === PARTICIPANT_ACTOR_KEY) {
        return {
          key: channelToKeyWithPrefix(PARTICIPANT_ACTOR_KEY, channel),
          color: '',
          role: participantLabel,
          channel
        };
      }
      const actor = d.supportingActors[actorKey];
      if (!actor) {
        console.warn('Unknown actor for task', { d, channel, actor: actorKey });
        return null;
      }
      return {
        key: channelToKeyWithPrefix(actorKey, channel),
        role: actor.name,
        color: '',
        channel
      };
    };
    const uniqChannels: ChannelDescriptor[] = uniqBy(
      compact([
        ...flatten(
          d.tasks.map((t) => t.channels.map((c) => toDescriptor(t.actor, c)))
        ),
        ...flatten(
          d.messages.other.map((m) =>
            m.channels.map((c) => toDescriptor(m.actor, c))
          )
        ),
        ...flatten(
          d.messages.outro
            ? d.messages.outro.channels.map((c) =>
                d.messages.outro
                  ? toDescriptor(d.messages.outro.actor, c)
                  : null
              )
            : []
        )
      ]),
      (x) => x.key
    );
    if (
      isEqual(
        Object.keys(prevResult.current),
        uniqChannels.map((c) => c.key)
      )
    ) {
      return prevResult.current;
    }
    const cs = [
      colors.red.w400,
      colors.yellow.w400,
      colors.blue.w400,
      colors.green.w400,
      colors.red.w700,
      colors.yellow.w700,
      colors.blue.w700,
      colors.green.w700,
      colors.red.w300,
      colors.yellow.w300,
      colors.blue.w300,
      colors.green.w300
    ];
    const nextResult = uniqChannels.reduce<{
      [key: string]: ChannelDescriptor;
    }>((m, c, i) => {
      const color = cs[i % cs.length];
      m[c.key] = {
        ...c,
        color
      };
      return m;
    }, {});
    prevResult.current = nextResult;
    return nextResult;
  }, [d, colors, participantLabel]);
};

const ChannelLegendContainer = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: p.theme.spacing(2),
  gap: p.theme.spacing(3)
}));

// this will also be able to do filtering later
export const ChannelLegend = React.memo(
  ({
    descriptors,
    children
  }: {
    descriptors: { [key: string]: ChannelDescriptor };
    children?: React.ReactNode;
  }) => {
    const { t } = useUnsafeTranslation();
    const channels = useMemo(() => {
      return Object.values(descriptors).map((d) => ({
        label: `${d.role} (${channelToString(d.channel, t)})`,
        color: d.color,
        key: d.key
      }));
    }, [descriptors, t]);

    return (
      <ChannelLegendContainer>
        {channels.map((c) => (
          <WithShape key={c.key} shape="square" color={c.color}>
            <strong>{c.label}</strong>
          </WithShape>
        ))}
        {children}
      </ChannelLegendContainer>
    );
  }
);

const COMMON_SVG_PROPS: SVGProps<SVGSVGElement> = {
  fill: 'none',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round'
};

export const ChannelIcon = ({
  channel,
  color = 'currentColor',
  onClick,
  size = 24,
  ...rest
}: {
  channel: ScheduleChannel;
  onClick?: () => void;
  size?: string | number;
  color?: string;
}) => {
  const { t } = useUnsafeTranslation();
  const body = () => {
    if (channel.type === ScheduleChannelType.SLACK) {
      return <Slack color={color} size={size} {...rest} />;
    }
    if (channel.type === ScheduleChannelType.MS_TEAMS) {
      return <TeamsIcon height={size} width={size} {...rest} />;
    }
    if (channel.type === ScheduleChannelType.SMS) {
      return <MessageSquare color={color} size={size} {...rest} />;
    }
    if (channel.type === ScheduleChannelType.EMAIL) {
      if (channel.emailFrom === 'email') {
        return (
          <MailWork
            {...COMMON_SVG_PROPS}
            width={size}
            height={size}
            stroke={color}
            {...rest}
          />
        );
      }
      if (channel.emailFrom === 'personalEmail') {
        return (
          <MailPersonal
            {...COMMON_SVG_PROPS}
            width={size}
            height={size}
            stroke={color}
            {...rest}
          />
        );
      }
      return <Mail color={color} size={size} {...rest} />;
    }

    return null;
  };
  const b = body();
  return b ? (
    <Tooltip title={channelToString(channel, t) || ''}>
      {onClick ? (
        <IconButton size="small" onClick={onClick}>
          {b}
        </IconButton>
      ) : (
        <span>{b}</span>
      )}
    </Tooltip>
  ) : (
    b
  );
};

// can later make them overlap mayb
const IconContainer = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: p.theme.spacing(0.5)
}));

export const ChannelIcons = ({
  channels,
  size,
  onClick,
  ...rest
}: {
  channels: ScheduleChannel[];
  size?: string | number;
  onClick?: (channel: ScheduleChannel) => void;
}) => {
  return (
    <IconContainer>
      {channels.map((d, i) => (
        <ChannelIcon
          key={i}
          channel={d}
          size={size}
          onClick={onClick ? () => onClick(d) : undefined}
          {...rest}
        />
      ))}
    </IconContainer>
  );
};
